import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useQuery, useLazyQuery } from '@apollo/client';

import { Box, Grid, Checkbox } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import classNames from 'classnames/bind';

import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';

import CustomTextField from '../../../../../components/Textfield/CustomTextField';
import SwitchCustom from '../../../../../Switch/SwitchCustom';
import CustomDropdown from '../../../../../components/Dropdown/CustomDropdown';
import colors from '../../../../../../constants/colors';
import CustomButtons from '../../../../../components/Button/CustomButtons';
import * as Q from '../../../../../../constants/graphqlQueries';

const useStyles = makeStyles(() => ({
    rowFooter: {
        display: 'flex',
        justifyContent: 'space-evenly',
        padding: '0 20px',
    },
    container: {
        display: 'flex',
        marginTop: '5px',
    },
    boxLayoutTitle: {
        fontWeight: '700',
        lineHeight: '20px',
        color: colors.blue,
        marginBottom: '12px',
        fontSize: '1.8rem',
    },
    boxDivLayout: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        margin: '0 3rem 0 0'
    },
    row: {
        display: 'block',
        margin: '1rem 0',
    },
    fieldTitle: {
        fontWeight: '400',
        fontSize: '1.4rem',
        marginBottom: '14px',
    },
    footerButtonContainer: {
        marginBottom: '16px',
    },
    ListItemButton: {
        '&:hover': {
            backgroundColor: 'transparent !important',
        },
    },
    boxContainerLayout: {
        display: 'flex',
        marginLeft: '1rem'
    },
    skeleton: {
        transform: 'none'
    }
}));

const sortItems = (a, b) => {
    const nameA = a.ScheduleSegmentDescription.toUpperCase();
    const nameB = b.ScheduleSegmentDescription.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
};
const optionsSwitch = {
    leftTitle: 'Container',
    rightTitle: 'Segment',
};
const FieldIndex = { codeLabel: 1, codeDescription: 2, availableCodes: 3 };

const ScheduleCodeMapping = ({ codeMappingId, handleClearEdit, handleSubmit, error }) => {
    const classes = useStyles();
    const cx = classNames.bind(classes);

    const [isSaveDisabled, setIsSaveDisabled] = useState(!codeMappingId);
    const [inputFieldDisabled, setInputFieldDisabled] = useState(0);
    const [codeLabel, setCodeLabel] = useState('');
    const [codeDescription, setCodeDescription] = useState('');
    const [segmentChecked, setSegmentChecked] = useState(false);

    const [selectedSourceSystem, setSelectedSourceSystem] = useState();
    const [sourceSystems, setSourceSystems] = useState({});
    const [sourceSystemsId, setSourceSystemsId] = useState();
    const [availableCodes, setAvailableCodes] = useState([]);
    const [selectedAvailableCodes, setSelectedAvailableCodes] = useState([]);

    const [filteredAvailableCodes, setFilteredAvailableCodes] = useState(availableCodes);
    const [filteredSelectedAvailableCodes, setFilteredSelectedAvailableCodes] = useState(selectedAvailableCodes);

    const [filterAvailableCodes, setFilterAvailableCodes] = useState('');
    const [filterSelectedAvailableCodes, setFilterSelectedAvailableCodes] = useState('');

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const { data: dataSourceSystems } = useQuery(Q.CODE_MAPPING_SOURCE_SYSTEMS, { variables: { SourceSystemId: sourceSystemsId } });
    // lazy queries
    const [getCodeMapping, { data: dataCodeMapping, loading: loadingCodeMapping }] = useLazyQuery(Q.CODE_MAPPING_QUERY);
    const [getAvailableCodes, { data: dataAvailableCodesSchedule }] = useLazyQuery(
        Q.CODE_MAPPING_SCHEDULE_AVALIBLE_CODES,
    );

    const handleChangeCodeLabel = value => setCodeLabel(value);
    const handleChangeCodeDescription = value => setCodeDescription(value);
    const handleSegmentChange = () => setSegmentChecked(!segmentChecked);
    const handleSelectedKey = newValueSelected => {
        const newValue = newValueSelected;
        newValue.SourceSystemId = selectedSourceSystem.SourceSystemId;
        setSelectedAvailableCodes([...selectedAvailableCodes, newValue].sort((a, b) => sortItems(a, b)));
        setAvailableCodes(availableCodes.filter((ac) => ac.ScheduleSegmentExternalKey !== newValueSelected.ScheduleSegmentExternalKey));
    };
    const handleUnselectedKey = valueSelected => {
        const newValue = valueSelected;
        setSelectedAvailableCodes(selectedAvailableCodes.filter((ac) => ac.ScheduleSegmentExternalKey !== valueSelected.ScheduleSegmentExternalKey));
        if (selectedSourceSystem && selectedSourceSystem.SourceSystemId === newValue.SourceSystemId) {
            delete newValue.SourceSystemId;
            setAvailableCodes([...availableCodes, newValue].sort((a, b) => sortItems(a, b)));
        }
    };
    const handleChangeFilterAvailableCodes = value => setFilterAvailableCodes(value);
    const handleChangeFilterSelectedAvailableCodes = value => setFilterSelectedAvailableCodes(value);
    const renderFilteredAvailableCodesRow = props => {
        // eslint-disable-next-line react/prop-types
        const { index, style } = props;
        if (index > filteredAvailableCodes.length - 1) return '';
        return (
            <ListItem
                button
                style={style}
                key={filteredAvailableCodes[index].ScheduleSegmentExternalKey}
                onClick={() => handleSelectedKey(filteredAvailableCodes[index])}
            >
                <ListItemText primary={filteredAvailableCodes[index].ScheduleSegmentDescription} />
            </ListItem>
        );
    };
    const renderFilteredSelectedAvailableCodesRow = props => {
        // eslint-disable-next-line react/prop-types
        const { index, style } = props;
        if (index > filteredSelectedAvailableCodes.length - 1) return '';
        return (
            <ListItem
                style={style}
                key={filteredSelectedAvailableCodes[index].ScheduleSegmentExternalKey}
                className={classes.ListItemButton}
            >
                <ListItemIcon>
                    <Checkbox
                        edge='start'
                        checked
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleUnselectedKey(filteredSelectedAvailableCodes[index])}
                    />
                </ListItemIcon>
                <ListItemText primary={filteredSelectedAvailableCodes[index].ScheduleSegmentDescription} />
            </ListItem>
        );
    };
    const handleChangeSourceSystem = newValue => {
        setSelectedSourceSystem(newValue);
        if (newValue) {
            getAvailableCodes({
                variables: {
                    SourceSystemId: newValue.SourceSystemId,
                },
            });
        } else {
            setAvailableCodes([]);
        }
    };
    const handleCancel = () => {
        setCodeLabel('');
        setCodeDescription('');
        setCodeDescription(true);
        setSelectedSourceSystem(null);
        setAvailableCodes([]);
        setSelectedAvailableCodes([]);
        setSegmentChecked(false);
        handleClearEdit();
    };
    const handleSave = () => {
        setLoadingSubmit(true);
        const input = {
            codeMappingId,
            codeLabel,
            codeDescription,
            segment: segmentChecked,
            selectedCodes: selectedAvailableCodes.map(
                (ac) => ({
                    SourceSystemId: ac.SourceSystemId,
                    codeId: ac.ScheduleSegmentExternalKey,
                }),
            ),
        }
        handleSubmit(input)
    };
    const isInputFieldDisabled = (inputIndex) => {
        if (inputFieldDisabled && inputIndex > inputFieldDisabled) {
            return true;
        };
        return false;
    };

    useEffect(() => {
        if (codeMappingId) getCodeMapping({ variables: { id: codeMappingId } });
    }, [codeMappingId]);
    useEffect(() => {
        if (sourceSystemsId) {
            setCodeLabel(dataCodeMapping.codeMapping.Label);
            setCodeDescription(dataCodeMapping.codeMapping.Description);
            setSegmentChecked(dataCodeMapping.codeMapping.ScheduleTypeId === 2); // TODO: make this more dynamic
            setSelectedAvailableCodes(dataCodeMapping.codeMapping.ScheduleSegments.map(ss => ({
                ScheduleSegmentExternalKey: ss.ScheduleSegmentExternalKey,
                SourceSystemId: parseInt(ss.SourceSystemId, 10),
                ScheduleSegmentDescription: ss.ScheduleSegment.ScheduleSegmentDescription,
                __typename: 'ScheduleSegment'
            })));
        };
        if (dataCodeMapping && sourceSystems) {
            const $ID = dataCodeMapping.codeMapping.ScheduleSegments.find(s => s.SourceSystemId);
            setSourceSystemsId(parseInt($ID.SourceSystemId, 10));
        };
    }, [dataCodeMapping, sourceSystems])
    useEffect(() => {
        if (dataSourceSystems) {
            const sourceSystemsProps = {
                options: dataSourceSystems.sourceSystems,
                getOptionLabel: (option) => option.Description
            }
            setSourceSystems(sourceSystemsProps);
            if (sourceSystemsId) handleChangeSourceSystem(dataSourceSystems.sourceSystems[0]);
        };
    }, [dataSourceSystems]);
    useEffect(() => {
        if (dataAvailableCodesSchedule) {
            const ordered = dataAvailableCodesSchedule.scheduleSegments.sort((a, b) => sortItems(a, b));
            const orderedAndFiltered = ordered.filter(
                (oac) => (
                    selectedAvailableCodes.findIndex((sac) => sac.ScheduleSegmentExternalKey === oac.ScheduleSegmentExternalKey) < 0
                ),
            );

            setAvailableCodes(orderedAndFiltered);
        }
    }, [dataAvailableCodesSchedule]);
    useEffect(() => {
        if (filterAvailableCodes.trim() !== '') {
            setFilteredAvailableCodes(availableCodes.filter((ac) => ac.ScheduleSegmentDescription.toUpperCase().startsWith(filterAvailableCodes.toUpperCase())));
        } else {
            setFilteredAvailableCodes(availableCodes);
        }
    }, [filterAvailableCodes, availableCodes]);
    useEffect(() => {
        if (filterSelectedAvailableCodes.trim() !== '') {
            setFilteredSelectedAvailableCodes(selectedAvailableCodes.filter((ac) => ac.ScheduleSegmentDescription.toUpperCase().startsWith(filterSelectedAvailableCodes.toUpperCase())));
        } else {
            setFilteredSelectedAvailableCodes(selectedAvailableCodes);
        }
    }, [filterSelectedAvailableCodes, selectedAvailableCodes]);
    useEffect(() => {
        // State watcher
        if (!codeLabel) setInputFieldDisabled(FieldIndex.codeLabel);
        else if (!codeDescription) setInputFieldDisabled(FieldIndex.codeDescription);
        else if (!selectedAvailableCodes.length) setInputFieldDisabled(FieldIndex.availableCodes);
        else setInputFieldDisabled(null);
    }, [codeLabel, codeDescription, availableCodes, selectedAvailableCodes])
    useEffect(() => {
        if (inputFieldDisabled === null) setIsSaveDisabled(false);
        else setIsSaveDisabled(true);
    }, [inputFieldDisabled]);
    useEffect(() => {
        if(error) setLoadingSubmit(false);
    }, [error]);

    return (
        <>
            <Grid className={classes.boxContainerLayout}>
                <Grid item className={classes.boxDivLayout}>
                    {loadingCodeMapping ? <Skeleton width={250} height={40} />
                    : <p className={classes.boxLayoutTitle}>Dimensions</p>}
                    <Grid container direction='column' className={classes.container}>
                        <CustomTextField
                            title='Code Label'
                            value={codeLabel}
                            handleChange={handleChangeCodeLabel}
                            placeholder='Input text'
                            mandatory
                            loading={loadingCodeMapping}
                            maxLength={25}
                        />
                        <CustomTextField
                            title='Code Description'
                            value={codeDescription}
                            handleChange={handleChangeCodeDescription}
                            placeholder='Input text'
                            disabled={isInputFieldDisabled(FieldIndex.codeDescription)}
                            mandatory
                            loading={loadingCodeMapping}
                            maxLength={100}
                        />
                        <Box className={classes.row}>
                            {
                                loadingCodeMapping ? <>
                                <Skeleton width='50%' height={40} /> 
                                <Skeleton width='100%' height={40} /> 
                                </>
                                : <>
                                    <div className={classes.fieldTitle}>
                                        Code Type
                                    </div>
                                    <Grid component='label' container alignItems='center' spacing={1}>
                                        <SwitchCustom
                                            options={optionsSwitch}
                                            handleSwitch={handleSegmentChange}
                                            switchStatus={segmentChecked}
                                            isDualOption
                                        />
                                    </Grid>
                            </>}
                        </Box>
                    </Grid>
                </Grid>
                <Grid item className={classes.boxDivLayout} xs={8}>
                    {loadingCodeMapping ? <Skeleton width={250} height={40} />
                    : <p className={classes.boxLayoutTitle}>Code Selection</p>}
                    <Grid className={classes.container}>
                        <Grid item className={classes.boxDivLayout} xs={6}>
                            <CustomDropdown
                                title='WFM Tool'
                                value={selectedSourceSystem}
                                handleChange={handleChangeSourceSystem}
                                defaultProps={sourceSystems}
                                disabled={isInputFieldDisabled(FieldIndex.availableCodes)}
                                mandatory
                                loading={loadingCodeMapping}
                            />
                            <CustomTextField
                                title='Available Codes'
                                value={filterAvailableCodes}
                                handleChange={handleChangeFilterAvailableCodes}
                                placeholder='Search'
                                disabled={isInputFieldDisabled(FieldIndex.availableCodes)}
                                inputIcon
                                loading={loadingCodeMapping}
                            />
                            {
                                loadingCodeMapping ? <Skeleton className={classes.skeleton} width={250} height={200} /> 
                            : <Box className='selection__box'>
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <FixedSizeList height={height} width={width} itemSize={46} itemCount={filteredAvailableCodes.length}>
                                            {renderFilteredAvailableCodesRow}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            </Box>
                            }
                        </Grid>
                        <Grid item className={classes.boxDivLayout} xs={6}>
                            <CustomTextField
                                title='Selected Codes'
                                value={filterSelectedAvailableCodes}
                                handleChange={handleChangeFilterSelectedAvailableCodes}
                                placeholder='Search'
                                inputIcon
                                loading={loadingCodeMapping}
                            />
                            {
                                loadingCodeMapping ? <Skeleton className={classes.skeleton} width={250} height={200} /> 
                            : <Box className='selection__box'>
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <FixedSizeList height={height} width={width} itemSize={46} itemCount={selectedAvailableCodes.length}>
                                            {renderFilteredSelectedAvailableCodesRow}
                                        </FixedSizeList>
                                    )}
                                </AutoSizer>
                            </Box>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item className={cx(classes.boxDivLayout, classes.footerButtonContainer)} xs={12}>
                <Grid container direction='row-reverse'>
                    <Grid item xs={4}>
                        <Box className={classes.rowFooter}>
                            <CustomButtons
                                handleClick={handleCancel}
                                name='Cancel'
                                loading={loadingCodeMapping}
                            />
                            <CustomButtons
                                handleClick={handleSave}
                                name='Save'
                                disabled={isSaveDisabled}
                                tooltipFree
                                loadingButton={loadingSubmit}
                                loading={loadingCodeMapping}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

ScheduleCodeMapping.propTypes = {
    codeMappingId: PropTypes.number,
    handleClearEdit: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    error: PropTypes.bool.isRequired
};

ScheduleCodeMapping.defaultProps = {
    codeMappingId: undefined
};

export default ScheduleCodeMapping;